import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import { ArrowLeftIcon, TruckIcon, ShieldCheckIcon, WrenchScrewdriverIcon, ExclamationTriangleIcon, CreditCardIcon } from "@heroicons/react/24/outline";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { fetchVehicle } from "../../../../api/vehicle";
import { fetchVehicleServices } from "../../../../api/vehicles/service";
import { fetchVehicleDamageReports } from "../../../../api/vehicles/damage";
import { fetchVehicleServiceIntervals } from "../../../../api/vehicles/service";
import { fetchVehicleInsurances } from "../../../../api/vehicles/insurance";
import { TinyDocumentSection } from "../../../../components/dashboard/documents/tinyDocumentSection";
import { VehicleCardsPanel } from "../../../../components/dashboard/vehicles/cards/vehicle-cards-panel";
import VehicleDamagePanel from "../../../../components/dashboard/vehicles/damage/vehicle-damage-panel";
import { VehicleServicePanel } from "../../../../components/dashboard/vehicles/service/vehicle-service-panel";
import { fetchChecklists } from "../../../../api/quality/checklist";
import { useSelector } from "react-redux";
import CurrentVehicleSection from "../currentVehicleSection";

/** Read-only vehicle checklists (same data as Internal Controls on the main vehicle page). */
const VehicleHandbookChecks = ({ vehicleId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [checklists, setChecklists] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    async function loadChecklists() {
      if (!vehicleId) return;
      setIsLoading(true);
      try {
        const response = await fetchChecklists({
          filters: [{ name: "vehicle_id", value: vehicleId }],
          per_page: 100,
          page: 1,
          sortBy: "created_at",
          sort: "DESC",
        });
        setChecklists(response?.data || []);
      } catch (e) {
        setChecklists([]);
      } finally {
        setIsLoading(false);
      }
    }
    loadChecklists();
  }, [vehicleId]);

  const getStatusColor = (status) => {
    switch (status) {
      case "open":
        return "bg-gray-100 text-gray-800";
      case "in_progress":
        return "bg-yellow-100 text-yellow-800";
      case "finished":
        return "bg-green-100 text-green-800";
      case "approved":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "open":
        return t("Open");
      case "in_progress":
        return t("In Progress");
      case "finished":
        return t("Finished");
      case "approved":
        return t("Approved");
      default:
        return status;
    }
  };

  if (isLoading) {
    return <div className="text-sm text-gray-500">{t("Loading...")}</div>;
  }

  if (checklists.length === 0) {
    return (
      <div className="text-center py-8">
        <ShieldCheckIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">{t("No checklists found")}</h3>
        <p className="mt-1 text-sm text-gray-500">{t("Vehicle safety and maintenance checklists")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-medium text-gray-900">{t("Internal Controls")}</h3>
        <p className="text-sm text-gray-500">{t("Vehicle safety and maintenance checklists")}</p>
      </div>
      <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white">
        {checklists.map((checklist) => (
          <li key={checklist.id}>
            <button
              type="button"
              onClick={() => navigate(`/dashboard/hse-ks/checklists/${checklist.id}`)}
              className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-gray-50"
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-gray-900">{checklist.number || checklist.title}</span>
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getStatusColor(checklist.status)}`}>
                    {getStatusText(checklist.status)}
                  </span>
                </div>
                {checklist.title && (
                  <p className="mt-1 truncate text-sm text-gray-500">{checklist.title}</p>
                )}
                <p className="mt-1 text-xs text-gray-400">{checklist.date}</p>
              </div>
              <svg className="ml-2 h-5 w-5 flex-shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const DriverVehicleHandbook = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.session.currentUser);

  const [vehicle, setVehicle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [latestService, setLatestService] = useState(null);
  const [latestDamage, setLatestDamage] = useState(null);
  const [nextService, setNextService] = useState(null);
  const [insurances, setInsurances] = useState([]);
  const activeInsurance = insurances.find((i) => i.active);

  useEffect(() => {
    async function loadAll() {
      setIsLoading(true);
      setError(null);
      try {
        const [vehRes, servicesRes, damagesRes, intervalsRes, insRes] = await Promise.all([
          fetchVehicle(id),
          fetchVehicleServices({ filters: [{ name: "vehicle_id", value: id }], per_page: 1, sortBy: "performed_at", sort: "DESC" }),
          fetchVehicleDamageReports({ vehicle_id: id, filters: [{ name: "vehicle_id", value: id }], per_page: 1, sortBy: "occurred_at", sort: "DESC" }),
          fetchVehicleServiceIntervals({ filters: [{ name: "vehicle_id", value: id }], per_page: 50 }),
          fetchVehicleInsurances({ filters: [{ name: "vehicle_id", value: id }], per_page: 10 }),
        ]);
        setVehicle(vehRes?.data || null);
        setLatestService((servicesRes?.data || [])[0] || null);
        setLatestDamage((damagesRes?.data || [])[0] || null);
        const intervals = intervalsRes?.data || [];
        if (intervals.length > 0) {
          const upcoming = intervals
            .map((i) => i.next_service_at)
            .filter(Boolean)
            .sort((a, b) => new Date(a) - new Date(b))[0];
          setNextService(upcoming || null);
        } else {
          setNextService(null);
        }
        setInsurances(insRes?.data || []);
      } catch (e) {
        setError("Failed to load vehicle handbook");
      } finally {
        setIsLoading(false);
      }
    }
    if (id) loadAll();
  }, [id]);

  const tabs = [
    { id: "overview", label: t("Overview"), icon: TruckIcon },
    { id: "damages", label: t("Damage Reports"), icon: ExclamationTriangleIcon },
    { id: "checks", label: t("Checks"), icon: ShieldCheckIcon },
    { id: "services", label: t("Services"), icon: WrenchScrewdriverIcon },
    { id: "cards", label: t("Cards & Services"), icon: CreditCardIcon },
  ];

  const renderOverview = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-500">{t("Vehicle")}</div>
              <div className="text-lg font-semibold text-gray-900">
                {vehicle?.number_plate} {vehicle?.model ? `- ${vehicle.model}` : ""}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-sm text-gray-500">{t("Latest Service")}</div>
          <div className="text-lg font-semibold text-gray-900">
            {latestService ? new Date(latestService.performed_at).toLocaleDateString() : t("No service yet")}
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-sm text-gray-500">{t("Latest Damage")}</div>
          <div className="text-lg font-semibold text-gray-900">
            {latestDamage ? new Date(latestDamage.occurred_at).toLocaleDateString() : t("No damage reports")}
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-sm text-gray-500">{t("Next Control")}</div>
          <div className="text-lg font-semibold text-gray-900">
            {vehicle?.next_inspection_date || "-"}
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-sm text-gray-500">{t("Next Service")}</div>
          <div className="text-lg font-semibold text-gray-900">
            {nextService ? new Date(nextService).toLocaleDateString() : (vehicle?.next_service_date || "-")}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4 max-w-full w-full">
        <div className="flex items-center justify-between w-full">
          <div>
            <div className="text-sm text-gray-500 min-w-full">{t("Insurance")}</div>
            {activeInsurance ? (
              <div>
                <div className="text-lg font-semibold text-gray-900">{activeInsurance.insurance_company}</div>
                <div className="text-sm text-gray-600">{t("Policy")}: {activeInsurance.policy_number}</div>
                <div className="text-sm text-gray-600">{t("Valid from")}: {activeInsurance.start_date}</div>
                {activeInsurance.end_date && (
                  <div className="text-sm text-gray-600">{t("Valid to")}: {activeInsurance.end_date}</div>
                )}
                <div className="mt-4 min-w-full">
                  <TinyDocumentSection
                    module="vehicle_insurance"
                    moduleParam={activeInsurance.id}
                    title={t("Insurance Documents")}
                    showNoDocuments={true}
                    refreshButton={false}
                    maxDocumentDisplay={8}
                  />
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-500">{t("No active insurance")}</div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="text-sm text-gray-500 mb-2">{t("Documents")}</div>
        <TinyDocumentSection
          module="vehicle"
          moduleParam={id}
          title={t("Vehicle Documents")}
          showNoDocuments={true}
          refreshButton={true}
          maxDocumentDisplay={8}
        />
      </div>
    </div>
  );

  const renderDamages = () => <VehicleDamagePanel key={`vehicle-damage-${id}`} vehicleId={id} canManage={false} />;

  const renderChecks = () => <VehicleHandbookChecks vehicleId={id} />;

  const renderServices = () => <VehicleServicePanel vehicleId={id} canManage={false} />;

  const renderCardsAndServices = () => <VehicleCardsPanel vehicleId={id} canManage={false} />;

  const renderContent = () => {
    if (isLoading) return <ResourceLoading />;
    if (error) return <ResourceError errors={[error]} />;
    switch (activeTab) {
      case "overview":
        return renderOverview();
      case "damages":
        return renderDamages();
      case "checks":
        return renderChecks();
      case "services":
        return renderServices();
      case "cards":
        return renderCardsAndServices();
      default:
        return renderOverview();
    }
  };

  return (
    <DashboardHeader
      subtitle=""
      title={t("Driver Handbook")}
      meta={[{ label: t("Vehicle"), icon: TruckIcon }]}
      mainButton={{ label: t("Back"), action: () => navigate(-1), path: null, color: "", icon: ArrowLeftIcon }}
    >
      <div className="mx-auto mt-8 w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        {currentUser?.driver && (
          <div className="mb-6">
            <CurrentVehicleSection />
          </div>
        )}
        <div className="mb-6">
          <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center space-x-2 px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                  activeTab === tab.id ? "bg-white text-blue-600 shadow-sm" : "text-gray-600 hover:text-gray-900"
                }`}
              >
                <tab.icon className="h-4 w-4" />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">{renderContent()}</div>
      </div>
    </DashboardHeader>
  );
};

export default DriverVehicleHandbook;
